import { createSelector } from "@reduxjs/toolkit";
import { normalize } from "normalizr";

import type { EntitiesState, State } from "metabase/redux/store";
import { getSettings } from "metabase/settings";
import Question from "metabase-lib/v1/Question";
import Database from "metabase-lib/v1/metadata/Database";
import Field, {
  type HydratedFieldDimension,
} from "metabase-lib/v1/metadata/Field";
import ForeignKey from "metabase-lib/v1/metadata/ForeignKey";
import Metadata from "metabase-lib/v1/metadata/Metadata";
import type Schema from "metabase-lib/v1/metadata/Schema";
import Table from "metabase-lib/v1/metadata/Table";
import { isVirtualCardId } from "metabase-lib/v1/metadata/utils/saved-questions";
import type { ParameterField } from "metabase-lib/v1/parameters/types";
import {
  getFieldValues,
  getRemappings,
} from "metabase-lib/v1/queries/utils/field";
import { isNumeric } from "metabase-lib/v1/types/utils/isa";
import type {
  Field as ApiField,
  Table as ApiTable,
  Card,
  DatabaseId,
  FieldId,
  FieldReference,
  FieldValue,
  Measure,
  Metric,
  NormalizedDatabase,
  NormalizedField,
  NormalizedForeignKey,
  NormalizedMeasure,
  NormalizedMetric,
  NormalizedSchema,
  NormalizedSegment,
  NormalizedTable,
  SchemaId,
  Segment,
  TableId,
} from "metabase-types/api";

import { type FieldEntity, FieldSchema } from "./schema";

/**
 * The slice these selectors read. Naming it here rather than taking the global
 * `State` keeps the shape this module depends on explicit, and lets a caller
 * that holds only the mirror use them.
 */
type MetadataState = { entities: EntitiesState };

type TableSelectorOpts = {
  includeHiddenTables?: boolean;
};

type FieldSelectorOpts = {
  includeSensitiveFields?: boolean;
};

export type MetadataSelectorOpts = TableSelectorOpts & FieldSelectorOpts;

const getNormalizedDatabases = (state: MetadataState) =>
  state.entities.databases;
const getNormalizedSchemas = (state: MetadataState) => state.entities.schemas;

const getNormalizedTablesUnfiltered = (state: MetadataState) =>
  state.entities.tables;

const getIncludeHiddenTables = (
  _state: MetadataState,
  props?: TableSelectorOpts,
) => !!props?.includeHiddenTables;

const getNormalizedTables = createSelector(
  [getNormalizedTablesUnfiltered, getIncludeHiddenTables],
  (tables, includeHiddenTables) =>
    includeHiddenTables
      ? tables
      : Object.fromEntries(
          Object.entries(tables).filter(
            ([, table]) => table.visibility_type == null,
          ),
        ),
);

const getNormalizedFieldsUnfiltered = (state: MetadataState) =>
  state.entities.fields;
const getIncludeSensitiveFields = (
  _state: MetadataState,
  props?: FieldSelectorOpts,
) => !!props?.includeSensitiveFields;

const getNormalizedFields = createSelector(
  [
    getNormalizedFieldsUnfiltered,
    getNormalizedTablesUnfiltered,
    getIncludeHiddenTables,
    getIncludeSensitiveFields,
  ],
  (fields, tables, includeHiddenTables, includeSensitiveFields) =>
    Object.fromEntries(
      Object.entries(fields).filter(([, field]) => {
        const table = tables[field.table_id];

        const shouldIncludeTable =
          !table || table.visibility_type == null || includeHiddenTables;

        const shouldIncludeField =
          field.visibility_type !== "sensitive" || includeSensitiveFields;

        return shouldIncludeTable && shouldIncludeField;
      }),
    ),
);

const getNormalizedSegments = (state: MetadataState) => state.entities.segments;
const getNormalizedMeasures = (state: MetadataState) =>
  state.entities.measures ?? {};
const getNormalizedMetrics = (state: MetadataState) =>
  state.entities.metrics ?? {};
const getNormalizedQuestions = (state: MetadataState) =>
  state.entities.questions;
const getNormalizedSnippets = (state: MetadataState) => state.entities.snippets;

export const getShallowDatabases = getNormalizedDatabases;
export const getShallowTables = getNormalizedTables;
export const getShallowFields = getNormalizedFields;
export const getShallowSegments = getNormalizedSegments;
export const getShallowSchemas = getNormalizedSchemas;
export const getShallowQuestions = getNormalizedQuestions;

const getVisibleTable = (state: MetadataState, tableId: TableId) =>
  getNormalizedTables(state)[tableId];

const getVisibleFields = (state: MetadataState) => getNormalizedFields(state);

// Field values can arrive before the field itself, which leaves a stub record
// with no `uniqueId`. `getMetadata` drops those.
const isResolvedField = (
  field: NormalizedField | undefined,
): field is NormalizedField => field?.uniqueId != null;

/**
 * The ids of a table's fields, as `getMetadata` resolves `Table.fields`: the
 * table's `original_fields` when it has them, otherwise its visible fields.
 */
export const getShallowTableFieldIds = createSelector(
  [getVisibleTable, getVisibleFields],
  (table, fields): ApiField["id"][] => {
    if (table == null) {
      return [];
    }
    if (table.original_fields) {
      return table.original_fields.map((field) => field.id);
    }
    return (table.fields ?? [])
      .map((fieldKey) => fields[fieldKey])
      .filter(isResolvedField)
      .map((field) => field.id);
  },
);

export type ShallowForeignKey = Omit<NormalizedForeignKey, "origin"> & {
  origin: NormalizedField;
};

/**
 * A table's foreign keys whose origin field is visible, with that field in
 * place of its id. `undefined` until the table's foreign keys are loaded.
 */
export const getShallowTableForeignKeys = createSelector(
  [getVisibleTable, getVisibleFields],
  (table, fields): ShallowForeignKey[] | undefined =>
    table?.fks?.flatMap((foreignKey) => {
      const origin = fields[foreignKey.origin_id];
      return isResolvedField(origin) ? [{ ...foreignKey, origin }] : [];
    }),
);

// The relations between the store's records need the same resolving the v1
// `Metadata` object does: a table that arrived without its ids still belongs to
// its schema, and `original_fields` outranks a table's field ids. Each selector
// below returns that lookup, memoised on the records behind it, so a caller
// that only learns the id it wants later still reads a stable function.

const getMetadataForShallowReads = (state: State) => getMetadata(state);

/**
 * A lookup from a database id to its schemas.
 */
export const getShallowDatabaseSchemas = createSelector(
  [getMetadataForShallowReads, getNormalizedSchemas],
  (metadata, schemas) =>
    (databaseId: DatabaseId): NormalizedSchema[] =>
      (metadata.database(databaseId)?.getSchemas() ?? [])
        .map((schema) => schemas[schema.id])
        .filter((schema) => schema != null),
);

/**
 * A lookup from a database id to its tables.
 */
export const getShallowDatabaseTables = createSelector(
  [getMetadataForShallowReads],
  (metadata) =>
    (databaseId: DatabaseId): NormalizedTable[] =>
      (metadata.database(databaseId)?.getTables() ?? []).map((table) =>
        table.getPlainObject(),
      ),
);

/**
 * A lookup from a schema id to its tables.
 */
export const getShallowSchemaTables = createSelector(
  [getMetadataForShallowReads],
  (metadata) =>
    (schemaId: SchemaId): NormalizedTable[] =>
      (metadata.schema(schemaId)?.tables ?? []).map((table) =>
        table.getPlainObject(),
      ),
);

/**
 * A lookup from a table id to the schema it belongs to. A table the store does
 * not hold, and a schema it has not loaded, both give nothing.
 */
export const getShallowTableSchema = createSelector(
  [getMetadataForShallowReads, getNormalizedSchemas],
  (metadata, schemas) =>
    (tableId: TableId): NormalizedSchema | undefined => {
      const schemaId = metadata.table(tableId)?.getPlainObject().schema;
      return schemaId != null ? schemas[schemaId] : undefined;
    },
);

/**
 * A lookup from a table id to its fields.
 */
export const getShallowTableFields = createSelector(
  [getMetadataForShallowReads],
  (metadata) =>
    (tableId: TableId): NormalizedField[] =>
      (metadata.table(tableId)?.getFields() ?? []).map((field) =>
        field.getPlainObject(),
      ),
);

/**
 * A lookup from a field id, or a field reference, to the field.
 */
export const getShallowFieldById = createSelector(
  [getMetadataForShallowReads],
  (metadata) =>
    (fieldId: FieldId | FieldReference): NormalizedField | undefined =>
      metadata.field(fieldId)?.getPlainObject(),
);

/**
 * A lookup from a field to its name, with the names of its parents for a
 * nested field. A field the store does not hold keeps its own `display_name`.
 */
export const getShallowFieldName = createSelector(
  [getMetadataForShallowReads],
  (metadata) =>
    (
      field: Pick<NormalizedField, "id" | "table_id" | "display_name">,
    ): string =>
      metadata.field(field.id, field.table_id)?.displayName() ??
      field.display_name,
);

// Takes the whole `State`, not `MetadataState`: it composes `getSettings`,
// which reads settings out of the RTK Query cache. It narrows when that does.
const getMetadataForOpts: (
  state: State,
  props: MetadataSelectorOpts,
) => Metadata = createSelector(
  [
    getNormalizedDatabases,
    getNormalizedSchemas,
    getNormalizedTables,
    getNormalizedFields,
    getNormalizedSegments,
    getNormalizedMeasures,
    getNormalizedMetrics,
    getNormalizedQuestions,
    getNormalizedSnippets,
    getSettings,
  ],
  (
    databases,
    schemas,
    tables,
    fields,
    segments,
    measures,
    metrics,
    questions,
    snippets,
    settings,
  ) => {
    const metadata = new Metadata({ settings });

    metadata.databases = Object.fromEntries(
      Object.values(databases).map((d) => [d.id, createDatabase(d, metadata)]),
    );
    metadata.schemas = Object.fromEntries(
      Object.values(schemas).map((s) => [s.id, createSchema(s, metadata)]),
    );
    metadata.tables = Object.fromEntries(
      Object.values(tables).map((t) => [t.id, createTable(t, metadata)]),
    );
    metadata.fields = Object.fromEntries(
      Object.values(fields)
        .filter((f) => f.uniqueId != null) // remove stub field instances created for field values without field properties
        .map((f) => [f.uniqueId, createField(f, metadata)]),
    );
    metadata.segments = Object.fromEntries(
      Object.values(segments).map((s) => [s.id, createSegment(s)]),
    );
    metadata.measures = Object.fromEntries(
      Object.values(measures).map((m) => [m.id, createMeasure(m)]),
    );
    metadata.metrics = Object.fromEntries(
      Object.values(metrics).map((m) => [m.id, createMetric(m)]),
    );
    metadata.questions = Object.fromEntries(
      Object.values(questions).map((c) => [c.id, createQuestion(c, metadata)]),
    );
    metadata.snippets = Object.fromEntries(
      Object.values(snippets).map((snippet) => [snippet.id, snippet]),
    );

    Object.values(metadata.databases).forEach((database) => {
      database.tables = hydrateDatabaseTables(database, metadata);
    });
    Object.values(metadata.schemas).forEach((schema) => {
      schema.database = hydrateSchemaDatabase(schemas[schema.id], metadata);
    });
    Object.values(metadata.tables).forEach((table) => {
      table.db = hydrateTableDatabase(table, metadata);
      table.schema = hydrateTableSchema(table, metadata);
      table.fields = hydrateTableFields(table, metadata);
      table.fks = hydrateTableForeignKeys(table, metadata);
      table.segments = hydrateTableSegments(table, metadata);
      table.measures = hydrateTableMeasures(table, metadata);
      table.metrics = hydrateTableMetrics(table, metadata);
    });
    Object.values(metadata.databases).forEach((database) => {
      database.schemas = hydrateDatabaseSchemas(database, metadata);
    });
    Object.values(metadata.schemas).forEach((schema) => {
      schema.tables = hydrateSchemaTables(schema, schemas[schema.id], metadata);
    });
    Object.values(metadata.measures).forEach((measure) => {
      measure.table = hydrateMeasureTable(measure, tables);
    });
    Object.values(metadata.fields).forEach((field) => {
      hydrateField(field, metadata);
    });
    Object.values(metadata.tables).forEach((table) => {
      table.fields?.forEach((field) => hydrateField(field, metadata));
    });

    return metadata;
  },
);

const NO_OPTS: MetadataSelectorOpts = {};

/**
 * Reselect keys its cache on the argument list, so `getMetadata(state)` and
 * `getMetadata(state, undefined)` would build two `Metadata` objects over the
 * same records, and with them two sets of metabase-lib caches. Callers use both
 * shapes, so the options are canonicalised here.
 */
export const getMetadata = (
  state: State,
  props: MetadataSelectorOpts = NO_OPTS,
): Metadata => getMetadataForOpts(state, props);

const UNFILTERED_OPTS: MetadataSelectorOpts = {
  includeHiddenTables: true,
  includeSensitiveFields: true,
};

export const getMetadataUnfiltered = (state: State) =>
  getMetadata(state, UNFILTERED_OPTS);

// Utils

function isNotNull<T>(value: T | null | undefined): value is T {
  return value != null;
}

function createDatabase(
  database: NormalizedDatabase,
  metadata: Metadata,
): Database {
  const instance = new Database(database);
  instance.metadata = metadata;
  return instance;
}

function createSchema(schema: NormalizedSchema, metadata: Metadata): Schema {
  const { database: _database, tables: _tables, ...rest } = schema;
  return { ...rest, metadata };
}

function createTable(table: NormalizedTable, metadata: Metadata): Table {
  const instance = new Table(table);
  instance.metadata = metadata;
  return instance;
}

function createField(field: NormalizedField, metadata: Metadata): Field {
  const instance = new Field(field);
  instance.metadata = metadata;
  return instance;
}

function createForeignKey(
  foreignKey: NormalizedForeignKey,
  metadata: Metadata,
): ForeignKey {
  const instance = new ForeignKey(foreignKey);
  instance.metadata = metadata;
  return instance;
}

function createSegment(segment: NormalizedSegment): Segment {
  const { table: _normalizedTableId, ...rest } = segment;
  return rest;
}

function createMeasure(measure: NormalizedMeasure): Measure {
  const { table: _normalizedTableId, ...rest } = measure;
  return rest;
}

function createMetric(metric: NormalizedMetric): Metric {
  const { collection: _normalizedCollectionId, ...rest } = metric;
  return { ...rest, collection: null };
}

function createQuestion(card: Card, metadata: Metadata): Question {
  return new Question(card, metadata);
}

function hydrateDatabaseTables(
  database: Database,
  metadata: Metadata,
): Table[] {
  const tableIds = database.getPlainObject().tables ?? [];
  if (tableIds.length > 0) {
    return tableIds.map((tableId) => metadata.table(tableId)).filter(isNotNull);
  }

  return Object.values(metadata.tables).filter(
    (table) =>
      !isVirtualCardId(table.id) && table.schema && table.db_id === database.id,
  );
}

function hydrateDatabaseSchemas(
  database: Database,
  metadata: Metadata,
): Schema[] {
  const schemaIds = database.getPlainObject().schemas;
  if (schemaIds) {
    return schemaIds.map((s) => metadata.schema(s)).filter(isNotNull);
  }

  return Object.values(metadata.schemas).filter(
    (s) => s.database && s.database.id === database.id,
  );
}

function hydrateSchemaDatabase(
  normalized: NormalizedSchema,
  metadata: Metadata,
): Database | undefined {
  return metadata.database(normalized.database) ?? undefined;
}

function hydrateSchemaTables(
  schema: Schema,
  normalized: NormalizedSchema,
  metadata: Metadata,
): Table[] {
  const tableIds = normalized.tables;
  if (tableIds) {
    return tableIds.map((table) => metadata.table(table)).filter(isNotNull);
  } else if (schema.database && schema.database.getTables().length > 0) {
    return schema.database
      .getTables()
      .filter((table) => table.schema_name === schema.name);
  } else {
    return Object.values(metadata.tables).filter(
      (table) => table.schema && table.schema.id === schema.id,
    );
  }
}

function hydrateTableDatabase(
  table: Table,
  metadata: Metadata,
): Database | undefined {
  const { db, db_id } = table.getPlainObject();
  return metadata.database(db ?? db_id) ?? undefined;
}

function hydrateTableSchema(
  table: Table,
  metadata: Metadata,
): Schema | undefined {
  const schemaId = table.getPlainObject().schema;
  return metadata.schema(schemaId) ?? undefined;
}

function hydrateTableFields(entityTable: Table, metadata: Metadata): Field[] {
  const apiTable = entityTable.getPlainObject();

  if (!apiTable.original_fields) {
    const fieldIds = apiTable.fields ?? [];
    return fieldIds.map((id) => metadata.field(id)).filter(isNotNull);
  }

  return apiTable.original_fields.map((apiField) => {
    // normalizing a single field always stores it under `result`
    const { entities, result } = normalize<
      FieldEntity,
      Pick<State["entities"], "fields">
    >(apiField, FieldSchema);
    const normalizedField = entities.fields?.[result];
    return createField(normalizedField, metadata);
  });
}

function hydrateField(field: Field, metadata: Metadata) {
  field.table = hydrateFieldTable(field, metadata);
  field.target = hydrateFieldTarget(field, metadata);
  field.name_field = hydrateNameField(field, metadata);
  field.dimensions = hydrateFieldDimensions(field, metadata);
  field.values = getFieldValues(field);
  field.remapping = new Map(getRemappings(field));
}

function hydrateTableForeignKeys(
  table: Table,
  metadata: Metadata,
): ForeignKey[] | undefined {
  return table.getPlainObject().fks?.map((fk) => {
    const instance = createForeignKey(fk, metadata);
    instance.origin = metadata.field(fk.origin_id) ?? undefined;
    instance.destination = metadata.field(fk.destination_id) ?? undefined;
    return instance;
  });
}

function hydrateTableSegments(table: Table, metadata: Metadata): Segment[] {
  const segmentIds = table.getPlainObject().segments ?? [];
  return segmentIds
    .map((id) => metadata.segments[id] ?? null)
    .filter(isNotNull);
}

function hydrateTableMeasures(table: Table, metadata: Metadata): Measure[] {
  const measureIds = table.getPlainObject().measures ?? [];
  return measureIds.map((id) => metadata.measure(id)).filter(isNotNull);
}

function hydrateTableMetrics(table: Table, metadata: Metadata): Question[] {
  const metricIds = table.getPlainObject().metrics ?? [];
  return metricIds.map((id) => metadata.question(id)).filter(isNotNull);
}

function hydrateFieldTable(
  field: Field,
  metadata: Metadata,
): Table | undefined {
  return metadata.table(field.table_id) ?? undefined;
}

function hydrateFieldTarget(
  field: Field,
  metadata: Metadata,
): Field | undefined {
  return metadata.field(field.fk_target_field_id) ?? undefined;
}

/**
 * Normalizing a field flattens each dimension's `human_readable_field` to an
 * id. The API nests the field itself, so it is put back here and a store field
 * answers a remapping question the same way an API field does.
 */
function hydrateFieldDimensions(
  field: Field,
  metadata: Metadata,
): HydratedFieldDimension[] {
  const dimensions = field.getPlainObject().dimensions ?? [];

  return dimensions.map((dimension) => ({
    ...dimension,
    human_readable_field:
      dimension.human_readable_field_id != null
        ? (metadata.field(dimension.human_readable_field_id) ?? undefined)
        : undefined,
  }));
}

function hydrateNameField(field: Field, metadata: Metadata): Field | undefined {
  const nameFieldId = field.getPlainObject().name_field;
  if (nameFieldId != null) {
    return metadata.field(nameFieldId) ?? undefined;
  }
}

function hydrateMeasureTable(
  measure: Measure,
  tables: Record<string, NormalizedTable>,
): ApiTable | undefined {
  const normalized = tables[measure.table_id];
  if (!normalized) {
    return undefined;
  }
  const {
    db: _db,
    fields: _fields,
    fks: _fks,
    segments: _segments,
    measures: _measures,
    metrics: _metrics,
    schema: _schema,
    schema_name,
    original_fields: _original_fields,
    ...rest
  } = normalized;
  return { ...rest, schema: schema_name ?? "" };
}

/**
 * A field's client-accumulated remappings. No endpoint returns these: they are
 * merged in as values are fetched, and one component's fetch labels values for
 * another, so a component cannot answer this from its own result.
 */
export function getFieldRemappings(
  state: MetadataState,
  fieldId: FieldId,
): FieldValue[] {
  return state.entities.fields[fieldId]?.remappings ?? NO_REMAPPINGS;
}

// a shared empty array, so a field with no remappings keeps its identity
// between calls and a `useSelector` on it does not re-render
const NO_REMAPPINGS: FieldValue[] = [];

/**
 * The label a field carries for `value`.
 *
 * It merges the values a values endpoint fetched with the remappings the
 * client accumulated as other widgets fetched values. The store holds both, so
 * a field a caller holds cannot answer this on its own.
 */
export function getRemappedFieldValue(
  state: State,
  field: ParameterField,
  value: unknown,
): string | undefined {
  const fieldId = typeof field.id === "number" ? field.id : null;
  const storeField =
    fieldId == null ? undefined : state.entities.fields[fieldId];
  const remappings =
    fieldId == null ? NO_REMAPPINGS : getFieldRemappings(state, fieldId);

  // parameter values arrive from the URL as strings
  const key =
    isNumeric(field) && typeof value !== "number"
      ? parseFloat(String(value))
      : value;

  // a later entry wins, so the accumulated remappings override the field's
  // own values
  const labels = new Map<unknown, string | undefined>(
    // the store's copy carries the values a values endpoint fetched, which the
    // field a caller holds does not
    getRemappings({ ...field, values: storeField?.values, remappings }),
  );

  return labels.get(key);
}
